import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AutoFixHighRounded, CheckCircleRounded, EditNoteRounded } from '@mui/icons-material';
import { useTenantStore } from '../../stores/tenant.store';
import {
  evaluationStandardApi,
  type StructuralScaffoldDto,
  type GenerateStandardResult,
  type AxisCoverageDto,
} from '../../lib/api/evaluation-standard.api';

// Assisted evaluation-standard authoring (Delivery 3a). The teacher picks a
// factory scaffold, writes their position in prose, and the platform STRUCTURES
// it — never expands. Vocabulary rule: the UI never says "IA"; it speaks of "la
// plataforma / sugerido / propuesta".

function extractReason(err: unknown): string | undefined {
  const details = (err as { details?: { reason?: string } })?.details;
  return details?.reason;
}

export function StandardAuthoringPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();

  const [scaffolds, setScaffolds] = useState<StructuralScaffoldDto[]>([]);
  const [loadingScaffolds, setLoadingScaffolds] = useState(true);
  const [scaffoldsError, setScaffoldsError] = useState<string | null>(null);

  const [scaffoldId, setScaffoldId] = useState('');
  const [title, setTitle] = useState('');
  const [prose, setProse] = useState('');
  const [catalogYear, setCatalogYear] = useState(String(new Date().getFullYear()));

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [draft, setDraft] = useState<GenerateStandardResult | null>(null);

  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);

  // Loading the scaffolds triggers the server-side self-reconciling seed. If it
  // loud-fails (no scaffolds), we surface it here — never a silent empty picker.
  useEffect(() => {
    if (!currentTenantId) return;
    let alive = true;
    setLoadingScaffolds(true);
    setScaffoldsError(null);
    evaluationStandardApi
      .listScaffolds({ tenantId: currentTenantId })
      .then((r) => {
        if (!alive) return;
        setScaffolds(r.scaffolds);
        if (r.scaffolds.length === 0) setScaffoldsError(t('standards.errors.noScaffolds'));
      })
      .catch(() => {
        if (alive) setScaffoldsError(t('standards.errors.noScaffolds'));
      })
      .finally(() => {
        if (alive) setLoadingScaffolds(false);
      });
    return () => {
      alive = false;
    };
  }, [currentTenantId, t]);

  const selectedScaffold = useMemo(
    () => scaffolds.find((s) => s.id === scaffoldId) ?? null,
    [scaffolds, scaffoldId],
  );

  const axisLabel = (axisId: string): string => {
    const axis = selectedScaffold?.axes.find((a) => a.id === axisId);
    return axis ? t(axis.label) : axisId;
  };

  const canGenerate = Boolean(scaffoldId && title.trim() && prose.trim() && !generating);

  async function handleGenerate(): Promise<void> {
    if (!currentTenantId || !canGenerate) return;
    setGenerating(true);
    setGenerateError(null);
    setApproved(false);
    try {
      const result = await evaluationStandardApi.generateDraft({
        tenantId: currentTenantId,
        scaffoldId,
        teacherProse: prose,
        title: title.trim(),
        catalogYear: catalogYear.trim(),
        locale: 'es',
      });
      setDraft(result);
    } catch (err) {
      // Only 'scaffold.not_found' is a legitimate outcome (the area isn't in the
      // factory set) — we tell the teacher we logged it. Every other reason is an
      // infra failure the platform couldn't complete ('scaffold.seed_failed' when
      // the set isn't seeded, or a model/parse failure) → the honest retry state.
      // We NEVER render a fabricated "coverage complete" on an infra failure.
      const reason = extractReason(err);
      setGenerateError(
        reason === 'scaffold.not_found'
          ? t('standards.errors.areaNotCovered')
          : t('standards.errors.generateFailed'),
      );
    } finally {
      setGenerating(false);
    }
  }

  async function handleApprove(): Promise<void> {
    if (!currentTenantId || !draft) return;
    setApproving(true);
    try {
      await evaluationStandardApi.approve({
        tenantId: currentTenantId,
        standardId: draft.standard.id,
      });
      setApproved(true);
    } catch {
      setGenerateError(t('standards.errors.approveFailed'));
    } finally {
      setApproving(false);
    }
  }

  const gaps = draft?.standard.content.axes.filter((a) => a.gap) ?? [];
  const covered = draft?.standard.content.axes.filter((a) => a.content && !a.gap) ?? [];

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t('standards.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('standards.subtitle')}
      </Typography>

      {scaffoldsError && !loadingScaffolds && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {scaffoldsError}
        </Alert>
      )}

      {/* Step 1 + 2: choose a scaffold and write the prose. */}
      {!approved && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Stack spacing={2.5}>
              <FormControl fullWidth disabled={loadingScaffolds || Boolean(scaffoldsError)}>
                <InputLabel id="scaffold-label">{t('standards.scaffoldPicker')}</InputLabel>
                <Select
                  labelId="scaffold-label"
                  label={t('standards.scaffoldPicker')}
                  value={scaffoldId}
                  onChange={(e) => {
                    setScaffoldId(e.target.value);
                    setDraft(null);
                  }}
                >
                  {scaffolds.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {t(s.title)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedScaffold && (
                <Typography variant="caption" color="text.secondary">
                  {t('standards.axesHint')}{' '}
                  {selectedScaffold.axes.map((a) => t(a.label)).join(' · ')}
                </Typography>
              )}

              <TextField
                label={t('standards.titleField')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
              />
              <TextField
                label={t('standards.catalogYearField')}
                value={catalogYear}
                onChange={(e) => setCatalogYear(e.target.value)}
                sx={{ maxWidth: 160 }}
              />
              <TextField
                label={t('standards.proseField')}
                placeholder={t('standards.prosePlaceholder')}
                value={prose}
                onChange={(e) => setProse(e.target.value)}
                multiline
                minRows={6}
                fullWidth
              />

              {generateError && <Alert severity="warning">{generateError}</Alert>}

              <Box>
                <Button
                  variant="contained"
                  startIcon={
                    generating ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <AutoFixHighRounded />
                    )
                  }
                  disabled={!canGenerate}
                  onClick={handleGenerate}
                >
                  {t('standards.generate')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Step 3: review the proposal with the author-vs-platform visual distinction. */}
      {draft && !approved && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('standards.reviewTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('standards.reviewHelp')}
            </Typography>

            {/* Legend for the visual distinction. */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Chip size="small" variant="outlined" label={t('standards.legend.yours')} />
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={t('standards.legend.suggested')}
              />
            </Stack>

            <Stack spacing={2}>
              {covered.map((axis) => (
                <AxisCoverageView
                  key={axis.axisId}
                  axis={axis}
                  label={axisLabel(axis.axisId)}
                  degraded={draft.degradedAxisIds.includes(axis.axisId)}
                  suggestedLabel={t('standards.legend.suggested')}
                  yoursLabel={t('standards.legend.yours')}
                  rewordedNote={t('standards.rewordedNote')}
                />
              ))}
            </Stack>

            {gaps.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  {t('standards.gapsTitle')}
                </Typography>
                <Stack spacing={1.5}>
                  {gaps.map((axis) => (
                    <Alert key={axis.axisId} severity="info" icon={<EditNoteRounded />}>
                      <Typography variant="subtitle2">{axisLabel(axis.axisId)}</Typography>
                      <Typography variant="body2">{axis.gap?.question}</Typography>
                    </Alert>
                  ))}
                </Stack>
                <Button
                  variant="text"
                  startIcon={<EditNoteRounded />}
                  sx={{ mt: 1 }}
                  onClick={() => setDraft(null)}
                >
                  {t('standards.completePosition')}
                </Button>
              </>
            )}

            {draft.standard.content.tensions.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  {t('standards.tensionsTitle')}
                </Typography>
                <ul style={{ marginTop: 0 }}>
                  {draft.standard.content.tensions.map((tension, i) => (
                    <li key={i}>
                      <Typography variant="body2">{tension}</Typography>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                color="success"
                startIcon={
                  approving ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <CheckCircleRounded />
                  )
                }
                disabled={approving}
                onClick={handleApprove}
              >
                {t('standards.approve')}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {t('standards.approveHint')}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      {approved && (
        <Alert severity="success" icon={<CheckCircleRounded />}>
          {t('standards.approvedMessage')}
        </Alert>
      )}

      <Divider sx={{ my: 4 }} />
      <PracticeBankManager />
    </Box>
  );
}

// Manage the self-study PRACTICE question bank of each approved standard (Frente
// B). One opening question per line; the student practices against a random one.
function PracticeBankManager(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [standards, setStandards] = useState<{ id: string; title: string; bank: string }[] | null>(
    null,
  );
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  useEffect(() => {
    if (!currentTenantId) return;
    let alive = true;
    evaluationStandardApi
      .list({ tenantId: currentTenantId })
      .then((r) => {
        if (!alive) return;
        setStandards(
          r.standards
            .filter((s) => s.status === 'approved')
            .map((s) => ({
              id: s.id,
              title: s.title,
              bank: (s.practiceRootQuestions ?? []).join('\n'),
            })),
        );
      })
      .catch(() => {
        if (alive) setStandards([]);
      });
    return () => {
      alive = false;
    };
  }, [currentTenantId]);

  const save = async (id: string, bank: string) => {
    if (!currentTenantId) return;
    setSavingId(id);
    setSavedId(null);
    try {
      const questions = bank
        .split('\n')
        .map((q) => q.trim())
        .filter((q) => q.length > 0);
      await evaluationStandardApi.savePracticeQuestions({
        tenantId: currentTenantId,
        standardId: id,
        questions,
      });
      setSavedId(id);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {t('standards.practiceBank.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('standards.practiceBank.subtitle')}
      </Typography>
      {standards === null && (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress />
        </Stack>
      )}
      {standards !== null && standards.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('standards.practiceBank.noApproved')}
        </Typography>
      )}
      <Stack spacing={2}>
        {(standards ?? []).map((s) => (
          <Card key={s.id} variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {s.title}
              </Typography>
              <TextField
                multiline
                minRows={3}
                fullWidth
                defaultValue={s.bank}
                placeholder={t('standards.practiceBank.placeholder')}
                onChange={(e) => (s.bank = e.target.value)}
                helperText={t('standards.practiceBank.hint')}
              />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={savingId === s.id}
                  startIcon={
                    savingId === s.id ? <CircularProgress size={14} color="inherit" /> : undefined
                  }
                  onClick={() => save(s.id, s.bank)}
                >
                  {t('standards.practiceBank.save')}
                </Button>
                {savedId === s.id && (
                  <Typography variant="caption" color="success.main">
                    {t('standards.practiceBank.saved')}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

function AxisCoverageView(props: {
  axis: AxisCoverageDto;
  label: string;
  degraded: boolean;
  suggestedLabel: string;
  yoursLabel: string;
  rewordedNote: string;
}): JSX.Element {
  const { axis, label, degraded, suggestedLabel, yoursLabel, rewordedNote } = props;
  // teacher_literal = the teacher's own words (neutral). Anything else = the
  // platform reworded/structured it (highlighted "suggested").
  const isLiteral = axis.provenance === 'teacher_literal';
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: isLiteral ? 'divider' : 'info.light',
        backgroundColor: isLiteral ? 'transparent' : 'rgba(2,136,209,0.06)',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2">{label}</Typography>
        <Chip
          size="small"
          variant="outlined"
          color={isLiteral ? 'default' : 'info'}
          label={isLiteral ? yoursLabel : suggestedLabel}
        />
      </Stack>
      <Typography variant="body2">{axis.content}</Typography>
      {degraded && (
        <Typography variant="caption" color="info.main">
          {rewordedNote}
        </Typography>
      )}
    </Box>
  );
}
